import {
  Component,
  DestroyRef,
  inject,
  Input,
  OnInit,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  AbstractControl,
  FormBuilder,
  ValidationErrors,
  Validators,
  ReactiveFormsModule,
} from '@angular/forms';
import { debounceTime, distinctUntilChanged, tap } from 'rxjs';
import { CustomValidators } from '@nimmsta/ng-commons-lib/utils/custom-validators';
import { InputWithErrorComponent } from '@nimmsta/ng-commons-lib/forms';
import { CsvDataConnection } from './csvDataConnection';
import { DataConnectionService } from '../../../services/dataConnection.service';
import { TranslatePipe } from '@ngx-translate/core';
import { DataConnectionConfigurationComponent } from '../../dataconnection-configuration.component';

@Component({
  selector: 'app-csv-form',
  templateUrl: 'csv-form.component.html',
  imports: [
    ReactiveFormsModule,
    InputWithErrorComponent,
    TranslatePipe,
    DataConnectionConfigurationComponent,
  ],
})
export class CsvFormComponent implements OnInit {
  @ViewChild('config', { static: true })
  configInput!: DataConnectionConfigurationComponent;

  @Input() value: CsvDataConnection = {} as CsvDataConnection;

  private fb = inject(FormBuilder);
  private service = inject(DataConnectionService);
  private readonly destroyRef = inject(DestroyRef);

  public form = this.fb.group({
    name: [
      '',
      [Validators.required, Validators.minLength(2), Validators.maxLength(128)],
    ],
    identifier: [
      '',
      [
        Validators.required,
        Validators.minLength(2),
        Validators.maxLength(128),
        CustomValidators.patternValidator(/^[a-zA-Z0-9-]+$/, {
          noSpaceOrSpecial: true,
        }),
      ],
    ],
    description: [''],
    dataTemplate: [''],
    configuration: ['', this.validConfig],
    treatAsOneJob: [false],
    defaultClearExisting: [false],
  });

  ngOnInit(): void {
    const { configuration, ...value } = this.value;
    this.form.patchValue(value);
    const configurationJson =
      typeof configuration === 'string'
        ? configuration
        : JSON.stringify(configuration ?? {});
    this.form.controls.configuration.setValue(configurationJson);
    if (this.form.controls.configuration.valid) {
      const {
        treatAsOneJob = false,
        defaultClearExisting = false,
        ...config
      } = JSON.parse(configurationJson.trim() || '{}');
      this.form.patchValue({
        treatAsOneJob,
        defaultClearExisting,
        dataTemplate: config.dataTemplate ?? '',
        configuration: JSON.stringify(config),
      });
    }
    this.configInput.keyUpEvent
      .pipe(
        debounceTime(200),
        distinctUntilChanged(),
        tap(() => {
          if (
            !this.configInput.control.value ||
            (this.configInput.control.value as string).trim() === ''
          ) {
            this.configInput.control.setValidators(this.validConfig);
            this.configInput.control.updateValueAndValidity();
          } else if (this.value.dataConnectionHandler) {
            this.service
              .validate(
                this.value.dataConnectionHandler,
                (this.configInput.control.value as string).trim(),
              )
              .pipe(takeUntilDestroyed(this.destroyRef))
              .subscribe({
                next: () => {
                  this.configInput.control.setValidators(this.validConfig);
                  this.configInput.control.updateValueAndValidity();
                },
                error: () => {
                  this.configInput.control.setValidators(this.invalidConfig);
                  this.configInput.control.updateValueAndValidity();
                },
              });
          }
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  getConfiguration(): string {
    const config = JSON.parse(
      this.form.controls.configuration.value?.trim() || '{}',
    );
    config.treatAsOneJob = this.form.get('treatAsOneJob')!.value;
    config.defaultClearExisting = this.form.get('defaultClearExisting')!.value;
    config.dataTemplate = this.form.get('dataTemplate')!.value;
    return JSON.stringify(config);
  }

  private validConfig(control: AbstractControl): ValidationErrors | null {
    try {
      const config = JSON.parse(control.value?.trim() || '{}');
      if (config && typeof config === 'object' && !Array.isArray(config)) {
        return null;
      }
    } catch {
      // Keep malformed input visible so it can be corrected in the editor.
    }
    return { 'dataconnection.invalid': true };
  }

  private invalidConfig(): ValidationErrors {
    return { 'dataconnection.invalid': true };
  }
}
